import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Calculator as CalcIcon, AlertCircle, Lightbulb, Download, FileDown, CheckCircle, Info, Settings2, ExternalLink, Database, FlaskConical, Users, DollarSign } from 'lucide-react';
import { CNCTestCutModal, TimeEstimator } from '@/components/CalibrationTools';

const setActiveJobContext = (ctx) => {
  try {
    localStorage.setItem('cnc:active_job_context', JSON.stringify(ctx));
  } catch {
    // ignore
  }
};

const Calculator = () => {
  const [formData, setFormData] = useState({
    material: 'softwood_pine',
    tool_diameter: 6.35,  // Legacy - now used as cutting_diameter
    flute_count: 2,
    spindle_rpm: '',
    desired_chip_load: '',

    tool_type: 'end_mill',
    bit_direction: 'upcut',
    spindle_type: 'medium_spindle',

    machine_rigidity: 'medium',
    tool_sharpness: 'new',
    
    // Enhanced tool geometry
    shank_diameter: 6.35,      // Shank diameter (for collet)
    cutting_diameter: 6.35,    // Actual cutting width
    cutting_length: 22,        // Max cutting depth
    v_angle: null,             // V-bit angle (if applicable)
  });

  const [result, setResult] = useState(null);
  const [resumeData, setResumeData] = useState(() => {
    try {
      const raw = localStorage.getItem('cnc:resume_calc');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  // Machine profiles state
  const [userMachines, setUserMachines] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState('');
  const [loadingMachines, setLoadingMachines] = useState(false);

  // User imported tools state
  const [userTools, setUserTools] = useState([]);
  const [selectedUserToolId, setSelectedUserToolId] = useState('');
  const [loadingUserTools, setLoadingUserTools] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showTestCutModal, setShowTestCutModal] = useState(false);
  const [exportTarget, setExportTarget] = useState('aspire');
  const [unitSystem, setUnitSystem] = useState('mm'); // 'mm' or 'inch'

  // Costing state
  const [currency, setCurrency] = useState(() => localStorage.getItem('cnc:currency') || 'ZAR');
  const [costInputs, setCostInputs] = useState({
    machineRate: '', jobHours: '', jobMinutes: '',
    elecWatts: '', elecRate: '', materialCost: '',
    wearRate: '', markupPercent: '',
    bitCost: '', bitPasses: ''
  });

  const currencySymbols = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };
  const sym = currencySymbols[currency] || currency;

  const handleCurrencyChange = (val) => {
    setCurrency(val);
    localStorage.setItem('cnc:currency', val);
  };

  const setCost = (field, val) => setCostInputs(prev => ({ ...prev, [field]: val }));

  const costCalc = useMemo(() => {
    const totalMinutes = (parseFloat(costInputs.jobHours) || 0) * 60 + (parseFloat(costInputs.jobMinutes) || 0);
    if (totalMinutes <= 0) return null;
    const totalHours = totalMinutes / 60;
    const machineCost = (parseFloat(costInputs.machineRate) || 0) * totalHours;
    const elecCost = ((parseFloat(costInputs.elecWatts) || 0) / 1000) * totalHours * (parseFloat(costInputs.elecRate) || 0);
    const materialCost = parseFloat(costInputs.materialCost) || 0;
    const bitCost = parseFloat(costInputs.bitCost) || 0;
    const bitPasses = parseFloat(costInputs.bitPasses) || 1;
    const bitWear = bitCost / bitPasses;
    const wearCost = machineCost * ((parseFloat(costInputs.wearRate) || 0) / 100);
    const subtotal = machineCost + elecCost + materialCost + bitWear + wearCost;
    const markupAmount = subtotal * ((parseFloat(costInputs.markupPercent) || 0) / 100);
    const total = subtotal + markupAmount;
    const perMinute = total / totalMinutes;
    return { machineCost, elecCost, materialCost, bitWear, wearCost, subtotal, markupAmount, total, perMinute };
  }, [costInputs]);

  // Unit conversion constants
  const navigate = useNavigate();

  const MM_TO_INCH = 0.0393701;
  const INCH_TO_MM = 25.4;

  const [searchParams] = useSearchParams();

  // Restore previous calculation from Recent Jobs “Reload” OR prefill from query params
  useEffect(() => {
    try {
      const raw = localStorage.getItem('cnc:last_calculation');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.formData) setFormData(parsed.formData);
        if (parsed?.unitSystem) setUnitSystem(parsed.unitSystem);
        localStorage.removeItem('cnc:last_calculation');
      }

      // Prefill from tool database link
      const next = {};
      const toolDiameter = searchParams.get('tool_diameter');
      const fluteCount = searchParams.get('flute_count');
      const toolType = searchParams.get('tool_type');
      const bitDirection = searchParams.get('bit_direction');
      const spindleRpm = searchParams.get('spindle_rpm');
      const desiredChip = searchParams.get('desired_chip_load');

      if (toolDiameter) next.tool_diameter = parseFloat(toolDiameter);
      if (fluteCount) next.flute_count = parseInt(fluteCount, 10);
      if (toolType) next.tool_type = toolType;
      if (bitDirection) next.bit_direction = bitDirection;
      if (spindleRpm) next.spindle_rpm = parseInt(spindleRpm, 10);
      if (desiredChip) next.desired_chip_load = parseFloat(desiredChip);

      if (Object.keys(next).length) {
        setFormData((prev) => ({ ...prev, ...next }));
      }
    } catch {
      // ignore
    }
    // (deps intentionally omitted for one-time restore)
  }, [searchParams]);
  // Fetch and apply onboarding defaults
  useEffect(() => {
    const applyOnboardingDefaults = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/onboarding`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = res.data;
        if (data?.completed) {
          // Apply onboarding defaults
          const updates = {};
          
          // Unit system
          if (data.units === 'imperial') {
            setUnitSystem('inch');
          }
          
          // Machine class -> rigidity
          if (data.machine_class === 'hobby') {
            updates.machine_rigidity = 'light';
          } else if (data.machine_class === 'heavy') {
            updates.machine_rigidity = 'heavy';
          } else {
            updates.machine_rigidity = 'medium';
          }
          
          // Spindle type
          if (data.spindle_type === 'trim_router') {
            updates.spindle_type = 'trim_router';
          } else {
            updates.spindle_type = 'medium_spindle';
          }
          
          // Typical material
          if (data.typical_material) {
            const materialMap = {
              'softwood': 'softwood_pine',
              'hardwood': 'hardwood_oak',
              'mdf_plywood': 'sheet_mdf',
              'plastics': 'plastic_acrylic',
              'aluminium': 'metal_aluminum'
            };
            if (materialMap[data.typical_material]) {
              updates.material = materialMap[data.typical_material];
            }
          }
          
          // CAM export target
          if (data.cam_export_target) {
            const exportMap = {
              'aspire': 'aspire',
              'fusion360': 'fusion360',
              'carbide_create': 'carbide'
            };
            setExportTarget(exportMap[data.cam_export_target] || 'aspire');
          }
          
          if (Object.keys(updates).length > 0) {
            setFormData(prev => ({ ...prev, ...updates }));
          }
        }
      } catch (e) {
        // Non-blocking - use default values
      }
    };
    
    applyOnboardingDefaults();
  }, [token]);
  // Fetch user's saved machines
  useEffect(() => {
    const fetchMachines = async () => {
      if (!token) return;
      setLoadingMachines(true);
      try {
        const res = await axios.get(`${API}/machines`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserMachines(res.data || []);
      } catch (err) {
        console.error('Failed to load machines', err);
      } finally {
        setLoadingMachines(false);
      }
    };
    fetchMachines();
  }, [token]);

  // Fetch user's imported tools (or app database if none)
  useEffect(() => {
    const fetchUserTools = async () => {
      if (!token) return;
      setLoadingUserTools(true);
      try {
        // Always fetch the application database (48 default tools)
        const defaultRes = await axios.get(`${API}/tools-v2/all`);
        const defaultTools = (defaultRes.data.tools || []).map(t => ({ ...t, isDefault: true }));
        
        // Also check if user has imported tools
        let userImportedTools = [];
        try {
          const userSourceRes = await axios.get(`${API}/tools-v2/user-source`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (userSourceRes.data.source === 'user_imported') {
            const userRes = await axios.get(`${API}/tools-v2`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            userImportedTools = (userRes.data.tools || []).map(t => ({ ...t, isImported: true }));
          }
        } catch (e) {
          // No imported tools, continue with defaults
        }
        
        // Combine: imported first, then defaults
        const combinedTools = [...userImportedTools, ...defaultTools];
        setUserTools(combinedTools);
        setToolSource(userImportedTools.length > 0 ? 'combined' : 'application_database');
      } catch (err) {
        console.error('Failed to load tools', err);
        // Fallback to tools-v2 API
        try {
          const res = await axios.get(`${API}/tools-v2`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUserTools(res.data.tools || []);
          setToolSource(res.data.source);
        } catch (e) {
          console.error('Fallback also failed', e);
        }
      } finally {
        setLoadingUserTools(false);
      }
    };
    fetchUserTools();
  }, [token]);

  // Track tool source (user_imported, application_database, or combined)
  const [toolSource, setToolSource] = useState('application_database');

  // Apply machine profile settings to form
  const applyMachineProfile = (machineId) => {
    setSelectedMachineId(machineId);
    if (!machineId) return;
    
    const machine = userMachines.find(m => m.id === machineId);
    if (!machine) return;

    const updates = {};

    // Map spindle type from machine profile
    const spindleType = machine.spindle_type_manual || machine.spindle_type_inferred;
    if (spindleType && spindleType !== 'unknown') {
      updates.spindle_type = spindleType;
    }

    // Map rigidity from machine profile
    if (machine.rigidity && machine.rigidity !== 'unknown') {
      updates.machine_rigidity = machine.rigidity;
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      toast.success(`Applied settings from "${machine.name}"`);
    } else {
      toast.info('Machine profile has no applicable settings');
    }
  };

  // Get selected machine name for display
  const selectedMachineName = useMemo(() => {
    if (!selectedMachineId) return null;
    const machine = userMachines.find(m => m.id === selectedMachineId);
    return machine?.name || null;
  }, [selectedMachineId, userMachines]);

  // Apply user imported tool to form
  const applyUserTool = (toolId) => {
    setSelectedUserToolId(toolId);
    if (!toolId) return;
    
    const tool = userTools.find(t => t.id === toolId);
    if (!tool) return;

    // Handle both v2 schema (cutting_diameter_mm) and legacy schema (diameter_mm)
    const cuttingDiameter = tool.cutting_diameter_mm || tool.diameter_mm;
    const shankDiameter = tool.shank_diameter_mm || cuttingDiameter;
    const cuttingLength = tool.cutting_length_mm || 22; // Default 22mm
    
    const updates = {
      tool_diameter: cuttingDiameter,  // Keep for backward compatibility
      cutting_diameter: cuttingDiameter,
      shank_diameter: shankDiameter,
      cutting_length: cuttingLength,
      flute_count: tool.flute_count,
      v_angle: tool.v_angle || null,
    };

    // Map tool type from category (v2) or tool_type (legacy)
    const toolCategory = tool.category || tool.tool_type;
    if (toolCategory) {
      const typeMap = {
        'End Mill': 'end_mill',
        'end_mill': 'end_mill',
        'Ball Nose': 'ball_nose',
        'ball_nose': 'ball_nose',
        'V-Bit': 'v_bit',
        'v_bit': 'v_bit',
        'Engraving': 'v_bit',
        'Drill': 'drill',
        'drill': 'drill',
        'Tapered Ball Nose': 'ball_nose',
        'Surfacing': 'surfacing',
        'compression': 'compression',
      };
      updates.tool_type = typeMap[toolCategory] || 'end_mill';
    }

    // Map flute direction if available (v2 schema)
    if (tool.flute_direction) {
      const directionMap = {
        'upcut': 'upcut',
        'downcut': 'downcut',
        'compression': 'compression',
        'straight': 'upcut',
      };
      updates.bit_direction = directionMap[tool.flute_direction] || 'upcut';
    }

    // Apply spindle RPM if available
    if (tool.spindle_rpm || tool.default_rpm) {
      updates.spindle_rpm = tool.spindle_rpm || tool.default_rpm;
    }

    setFormData(prev => ({ ...prev, ...updates }));
    
    // Show tool name with geometry info
    const displayName = tool.name_imperial || tool.name;
    toast.success(`Applied "${displayName}" - Cutting: ${cuttingDiameter}mm, Shank: ${shankDiameter}mm`);
  };

  // Get selected user tool name for display
  const selectedUserToolName = useMemo(() => {
    if (!selectedUserToolId) return null;
    const tool = userTools.find(t => t.id === selectedUserToolId);
    return tool?.name || null;
  }, [selectedUserToolId, userTools]);


  // Convert value based on unit system
  const toDisplayUnit = (valueInMm) => {
    if (unitSystem === 'inch') {
      return (valueInMm * MM_TO_INCH).toFixed(4);
    }
    return valueInMm;
  };

  const toMm = (value) => {
    if (unitSystem === 'inch') {
      return value * INCH_TO_MM;
    }
    return value;
  };

  // Get unit labels
  const getUnitLabel = (type = 'length') => {
    if (type === 'feedrate') {
      return unitSystem === 'inch' ? 'in/min' : 'mm/min';
    }
    return unitSystem === 'inch' ? 'in' : 'mm';
  };

  // Export target options
  const exportTargets = [
    { value: 'aspire', label: 'Vectric Aspire', extension: '.TOOL', format: 'Binary' },
    { value: 'fusion360', label: 'Fusion 360', extension: '.json', format: 'JSON' },
    { value: 'carbide', label: 'Carbide Create', extension: '.csv', format: 'CSV', beginner: true },
  ];

  // Organized materials by category
  const materialCategories = [
    {
      label: 'Softwoods',
      materials: [
        { value: 'softwood_pine', label: 'Pine' },
        { value: 'softwood_cedar', label: 'Cedar' },
        { value: 'softwood_spruce', label: 'Spruce' },
        { value: 'softwood_fir', label: 'Fir' },
        { value: 'softwood_redwood', label: 'Redwood' },
        { value: 'softwood_balsa', label: 'Balsa' },
      ]
    },
    {
      label: 'Hardwoods',
      materials: [
        { value: 'hardwood_oak', label: 'Oak' },
        { value: 'hardwood_walnut', label: 'Walnut' },
        { value: 'hardwood_maple', label: 'Maple' },
        { value: 'hardwood_cherry', label: 'Cherry' },
        { value: 'hardwood_ash', label: 'Ash' },
        { value: 'hardwood_birch', label: 'Birch' },
        { value: 'hardwood_mahogany', label: 'Mahogany' },
        { value: 'hardwood_teak', label: 'Teak' },
        { value: 'hardwood_hickory', label: 'Hickory' },
        { value: 'hardwood_beech', label: 'Beech' },
      ]
    },
    {
      label: 'Exotic Woods',
      materials: [
        { value: 'exotic_purpleheart', label: 'Purpleheart' },
        { value: 'exotic_ebony', label: 'Ebony' },
        { value: 'exotic_rosewood', label: 'Rosewood' },
        { value: 'exotic_padauk', label: 'Padauk' },
        { value: 'exotic_zebrawood', label: 'Zebrawood' },
        { value: 'exotic_wenge', label: 'Wenge' },
      ]
    },
    {
      label: 'Sheet Goods',
      materials: [
        { value: 'sheet_plywood', label: 'Plywood' },
        { value: 'sheet_mdf', label: 'MDF' },
        { value: 'sheet_hdf', label: 'HDF' },
        { value: 'sheet_particleboard', label: 'Particleboard' },
        { value: 'sheet_osb', label: 'OSB' },
        { value: 'sheet_melamine', label: 'Melamine' },
        { value: 'sheet_baltic_birch', label: 'Baltic Birch Plywood' },
      ]
    },
    {
      label: 'Plastics',
      materials: [
        { value: 'plastic_acrylic', label: 'Acrylic' },
        { value: 'plastic_polycarbonate', label: 'Polycarbonate' },
        { value: 'plastic_hdpe', label: 'HDPE' },
        { value: 'plastic_pvc', label: 'PVC' },
        { value: 'plastic_abs', label: 'ABS' },
        { value: 'plastic_nylon', label: 'Nylon' },
        { value: 'plastic_delrin', label: 'Delrin/Acetal' },
        { value: 'plastic_uhmw', label: 'UHMW' },
        { value: 'plastic_petg', label: 'PETG' },
      ]
    },
    {
      label: 'Metals',
      materials: [
        { value: 'metal_aluminum', label: 'Aluminum' },
        { value: 'metal_brass', label: 'Brass' },
        { value: 'metal_copper', label: 'Copper' },
        { value: 'metal_mild_steel', label: 'Mild Steel' },
        { value: 'metal_stainless', label: 'Stainless Steel' },
      ]
    },
    {
      label: 'Composites & Foam',
      materials: [
        { value: 'composite_carbon_fiber', label: 'Carbon Fiber' },
        { value: 'composite_fiberglass', label: 'Fiberglass' },
        { value: 'composite_g10', label: 'G10/FR4' },
        { value: 'foam_rigid', label: 'Rigid Foam' },
        { value: 'foam_eps', label: 'EPS Foam' },
        { value: 'foam_eva', label: 'EVA Foam' },
        { value: 'corian', label: 'Corian/Solid Surface' },
      ]
    },
  ];

  // Tool types
  const toolTypes = [
    { value: 'end_mill', label: 'End Mill (Flat)' },
    { value: 'ball_nose', label: 'Ball Nose' },
    { value: 'v_bit', label: 'V-Bit' },
    { value: 'straight', label: 'Straight Bit' },
    { value: 'spiral_upcut', label: 'Spiral Upcut' },
    { value: 'spiral_downcut', label: 'Spiral Downcut' },
    { value: 'spiral_compression', label: 'Spiral Compression' },
    { value: 'roughing', label: 'Roughing End Mill' },
    { value: 'finishing', label: 'Finishing End Mill' },
    { value: 'surfacing', label: 'Surfacing/Spoilboard' },
    { value: 'drill', label: 'Drill Bit' },
    { value: 'engraving', label: 'Engraving Bit' },
  ];

  // Bit directions
  const bitDirections = [
    { value: 'upcut', label: 'Upcut', description: 'Pulls chips up, great evacuation' },
    { value: 'downcut', label: 'Downcut', description: 'Pushes chips down, clean top edge' },
    { value: 'compression', label: 'Compression (Up/Down)', description: 'Clean edges top & bottom' },
  ];

  // Spindle types
  const spindleTypes = [
    { 
      value: 'trim_router', 
      label: 'Trim Router (Handheld)', 
      power: '500-900W',
      rpm: '16,000-32,000 RPM',
      description: 'Makita, Dewalt, Bosch trim routers. Best for: beginner wood CNC, MDF signs, shallow engraving'
    },
    { 
      value: 'small_water_cooled', 
      label: 'Small Water-Cooled Spindle', 
      power: '1.0-1.5 kW',
      rpm: '6,000-24,000 RPM',
      description: 'Popular first upgrade. Best for: hardwood cutting, light aluminium'
    },
    { 
      value: 'medium_spindle', 
      label: 'Standard Medium Spindle', 
      power: '1.8-2.2 kW',
      rpm: '6,000-24,000 RPM',
      description: 'Common hobby/prosumer choice. Best for: hardwoods, plastics, some aluminium'
    },
    { 
      value: 'high_power_spindle', 
      label: 'High-Power Industrial Spindle', 
      power: '2.2-3.0+ kW',
      rpm: '6,000-18,000 RPM',
      description: 'Pro workshop class. Best for: metals, deep aggressive cuts'
    },
  ];

  const machineRigidityOptions = [
    { value: 'light', label: 'Light (belt, hobby, flexible)', hint: 'More chatter risk. Use conservative DOC.' },
    { value: 'medium', label: 'Medium (prosumer)', hint: 'Balanced defaults.' },
    { value: 'heavy', label: 'Heavy (rigid, industrial)', hint: 'Can handle deeper cuts.' },
  ];

  const toolSharpnessOptions = [
    { value: 'new', label: 'New / Sharp', hint: 'Cleaner finish, lower burn risk.' },
    { value: 'used', label: 'Used / Dull-ish', hint: 'Higher burn + tear-out risk.' },
  ];

  const getExpertRisks = useMemo(() => (res) => {
    if (!res) return { burn: 'low', chatter: 'low', tearout: 'low', confidence: 'balanced', reasons: [], notes: [] };

    const warnings = res.warnings || [];
    const recs = res.recommendations || [];
    const all = [...warnings, ...recs].join(' ').toLowerCase();

    let burn = 'low';
    let chatter = 'low';
    let tearout = 'low';

    if (all.includes('burn risk') || all.includes('burn')) burn = 'high';
    if (all.includes('aggressive')) chatter = 'medium';

    // Heuristics (advisory):
    // - High RPM + low chipload -> burn risk
    // - Deep DOC relative to tool diameter + low rigidity -> chatter risk
    // - Upcut in softwood/hardwood + warning present -> tear-out/fuzz risk

    try {
      const rpm = Number(res.spindle_rpm);
      const chip = Number(res.chip_load);
      const doc = Number(res.pass_depth);
      const diaMm = unitSystem === 'inch' ? Number(formData.tool_diameter) * INCH_TO_MM : Number(formData.tool_diameter);
      const docRatio = diaMm ? doc / diaMm : 0;

      if (rpm >= 18000 && chip < 0.02 && burn !== 'high') burn = 'medium';

      const rigidityFactor = formData.machine_rigidity === 'light' ? 1 : (formData.machine_rigidity === 'heavy' ? 0 : 0.5);
      if (docRatio > 0.6 && rigidityFactor >= 0.5) chatter = 'high';
      else if (docRatio > 0.45 && chatter !== 'high') chatter = 'medium';

      const mat = String(formData.material);
      const isWood = mat.includes('softwood') || mat.includes('hardwood') || mat.includes('exotic') || mat.includes('sheet');
      if (isWood && formData.bit_direction === 'upcut') tearout = 'medium';
      if (isWood && formData.bit_direction === 'upcut' && docRatio > 0.6) tearout = 'high';

      if (formData.tool_sharpness === 'used') {
        burn = burn === 'low' ? 'medium' : burn;
        tearout = tearout === 'low' ? 'medium' : tearout;
      }
    } catch {
      // ignore
    }

    const reasons = [];
    if (formData.spindle_type === 'trim_router') reasons.push('Router class selected → conservative derating applied.');
    if (String(formData.material).includes('metal')) reasons.push('Metal category detected → RPM and strategy constraints applied.');

    const notes = [
      'Assumes typical hobby/prosumer CNC rigidity unless you select Light/Heavy rigidity.',
      'Sharp tooling and proper chip evacuation significantly affect burn/tear-out outcomes.',
      'Use test cuts and adjust based on sound, chip shape, and edge finish.'
    ];

    let confidence = 'balanced';
    const riskScore = (burn === 'high' || chatter === 'high' || tearout === 'high') ? 3 : (burn === 'medium' || chatter === 'medium' || tearout === 'medium') ? 2 : 1;
    if (riskScore === 1) confidence = 'conservative';
    else if (riskScore === 2) confidence = 'balanced';
    else confidence = 'aggressive';

    // If backend warns aggressive/burn risk, treat as high risk
    if (all.includes('burn risk') || all.includes('aggressive cut')) confidence = 'high_risk';

    return { burn, chatter, tearout, confidence, reasons, notes };
  }, [unitSystem, formData]);

  const risks = useMemo(() => getExpertRisks(result), [result, getExpertRisks]);


  const riskDotClass = (level) => (level === 'high' ? 'high' : (level === 'medium' ? 'medium' : 'low'));

  const confidenceLabel = (c) => {
    if (c === 'conservative') return 'Conservative / Beginner-safe';
    if (c === 'balanced') return 'Balanced / Recommended';
    if (c === 'aggressive') return 'Aggressive / Advanced';
    return 'High risk / verify cut';
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Convert tool diameter to mm if user is using inches
      const toolDiameterInMm = unitSystem === 'inch' 
        ? formData.tool_diameter * INCH_TO_MM 
        : formData.tool_diameter;
      
      // Prepare data, converting empty RPM to null
      const requestData = {
        ...formData,
        tool_diameter: toolDiameterInMm,
        spindle_rpm: formData.spindle_rpm === '' ? null : formData.spindle_rpm,
        desired_chip_load: formData.desired_chip_load === '' ? null : formData.desired_chip_load,
      };
      const response = await axios.post(`${API}/calculators/cutting-parameters`, requestData);

      // Normalise result — fill in derived fields the backend may not return so
      // the render never encounters undefined and never trips the error boundary.
      try {
        const raw = response.data;
        const normalised = {
          ...raw,
          recommended_pass_depth_mm: raw.recommended_pass_depth_mm ?? raw.pass_depth ?? 0,
          max_pass_depth_mm:         raw.max_pass_depth_mm ?? raw.pass_depth_max ?? raw.pass_depth ?? 0,
          pass_depth_max:            raw.pass_depth_max ?? raw.pass_depth ?? 0,
          chip_load:                 raw.chip_load ?? 0,
          cutting_direction:         raw.cutting_direction ?? '',
          warnings:                  Array.isArray(raw.warnings) ? raw.warnings : [],
          recommendations:           Array.isArray(raw.recommendations) ? raw.recommendations : [],
        };
        setResult(normalised);
      } catch {
        setResult(response.data);
      }

      // Save for Resume Last Job
      try {
        const resumeSnapshot = { formData: { ...formData }, unitSystem, savedAt: Date.now() };
        localStorage.setItem('cnc:resume_calc', JSON.stringify(resumeSnapshot));
        setResumeData(resumeSnapshot);
      } catch {}

      // Auto-save recent calculation snapshot (Recent Jobs) (non-blocking)
      try {
        if (token) {
          await axios.post(
            `${API}/calculations/history`,
            {
              user_id: user?.id || '',
              material: formData.material,
              tool_diameter: toolDiameterInMm,
              flute_count: formData.flute_count,
              spindle_rpm_input: formData.spindle_rpm === '' ? null : formData.spindle_rpm,
              tool_type: formData.tool_type,
              bit_direction: formData.bit_direction,
              spindle_type: formData.spindle_type,
              unit_system: unitSystem,
              spindle_rpm: response.data.spindle_rpm,
              feed_rate: response.data.feed_rate,
              plunge_rate: response.data.plunge_rate,
              pass_depth: response.data.pass_depth,
              chip_load: response.data.chip_load,
              is_saved: false,
              name: null,
              notes: null
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      } catch (e2) {
        // non-blocking
      }

      // Update Active Job Context (shop-foreman continuity)
      const warnings = response.data?.warnings || [];
      const joined = warnings.join(' ').toLowerCase();
      const safety = joined.includes('burn risk') || joined.includes('aggressive') ? 'crit' : (warnings.length ? 'warn' : 'safe');

      setActiveJobContext({
        profile: 'Default',
        units: unitSystem,
        material: formData.material,
        tool_diameter: toolDiameterInMm,
        flute_count: formData.flute_count,
        bit_direction: formData.bit_direction,
        safety,
        next: 'Export tool file or review troubleshooting checks before cutting.'
      });


      toast.success('Calculation complete!');
    } catch (error) {
      toast.error('Calculation failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Get result value in display units — null-safe to prevent render crashes
  const getResultValue = (valueInMm, decimals = 1) => {
    if (valueInMm == null || isNaN(Number(valueInMm))) return '—';
    if (unitSystem === 'inch') {
      return (Number(valueInMm) * MM_TO_INCH).toFixed(decimals);
    }
    return Number(valueInMm).toFixed(decimals);
  };

  // Get material display name
  const getMaterialName = () => {
    for (const category of materialCategories) {
      const found = category.materials.find(m => m.value === formData.material);
      if (found) return found.label;
    }
    return formData.material;
  };

  // Get spindle display name
  const getSpindleName = () => {
    const spindle = spindleTypes.find(s => s.value === formData.spindle_type);
    return spindle ? spindle.label : formData.spindle_type;
  };

  // Generate Aspire .tools XML file
  const generateAspireXML = () => {
    const materialName = getMaterialName();
    const spindleName = getSpindleName();
    const toolName = `CNCHelpdesk | ${materialName} | ${formData.tool_diameter}mm | ${formData.flute_count}F`;
    const cutDirection = result.cutting_direction.toLowerCase().includes('climb') ? 'climb' : 'conventional';
    
    // Get values in selected unit system
    const unitLabel = unitSystem === 'inch' ? 'inches' : 'mm';
    const feedUnitLabel = unitSystem === 'inch' ? 'in_per_min' : 'mm_per_min';
    const displayDiameter = unitSystem === 'inch' ? formData.tool_diameter : (formData.tool_diameter * INCH_TO_MM / INCH_TO_MM); // keep as entered
    const displayFeedRate = getResultValue(result.feed_rate, 2);
    const displayPlungeRate = getResultValue(result.plunge_rate, 2);
    const displayRampPlungeRate = getResultValue(result.ramp_plunge_rate, 2);
    const displayPassDepth = getResultValue(result.recommended_pass_depth_mm || result.pass_depth, 4);
    const displayPassDepthMax = getResultValue(result.max_pass_depth_mm || result.pass_depth_max, 4);
    const displayStepoverRough = getResultValue(result.stepover_rough, 4);
    const displayStepoverFinish = getResultValue(result.stepover_finish, 4);
    
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
<tool_database version="1">
  <tool type="endmill" name="${toolName}">
    <geometry>
      <diameter units="${unitLabel}">${formData.tool_diameter}</diameter>
      <flutes>${formData.flute_count}</flutes>
    </geometry>
    <feeds>
      <spindle_speed units="rpm">${result.spindle_rpm}</spindle_speed>
      <feed_rate units="${feedUnitLabel}">${displayFeedRate}</feed_rate>
      <plunge_rate units="${feedUnitLabel}">${displayPlungeRate}</plunge_rate>
      <ramp_plunge_rate units="${feedUnitLabel}">${displayRampPlungeRate}</ramp_plunge_rate>
    </feeds>
    <cutting>
      <pass_depth_recommended units="${unitLabel}">${displayPassDepth}</pass_depth_recommended>
      <pass_depth_max_safe units="${unitLabel}">${displayPassDepthMax}</pass_depth_max_safe>
      <stepover_rough units="percent">${result.stepover_rough_percent}</stepover_rough>
      <stepover_rough_value units="${unitLabel}">${displayStepoverRough}</stepover_rough_value>
      <stepover_finish units="percent">${result.stepover_finish_percent}</stepover_finish>
      <stepover_finish_value units="${unitLabel}">${displayStepoverFinish}</stepover_finish_value>
      <ramp_angle units="deg">${result.ramp_angle}</ramp_angle>
      <cut_direction>${cutDirection}</cut_direction>
    </cutting>
    <notes>
      Generated by CNC-Helpdesk
      
      *** IMPORTANT: ALL VALUES ARE IN ${unitSystem === 'inch' ? 'INCHES (in/min for feeds)' : 'MILLIMETERS (mm/min for feeds)'} ***
      
      Material: ${materialName}
      Chipload: ${result.chip_load.toFixed(4)} ${unitSystem === 'inch' ? 'in' : 'mm'}/tooth
      Spindle: ${spindleName}
      Bit Direction: ${formData.bit_direction}
      Tool Type: ${formData.tool_type.replace('_', ' ')}
    </notes>
  </tool>
</tool_database>`;
    
    return xml;
  };

  // Generate Fusion 360 .json tool library file
  const generateFusion360JSON = () => {
    const materialName = getMaterialName();
    const spindleName = getSpindleName();
    const unitLabel = unitSystem === 'inch' ? 'in' : 'mm';
    const toolName = `CNCHelpdesk | ${materialName} | ${formData.tool_diameter}${unitLabel} | ${formData.flute_count}F`;
    const productId = `cnchd_${materialName.toLowerCase().replace(/\s+/g, '_')}_${formData.tool_diameter}_${formData.flute_count}f`;
    const cutDirection = result.cutting_direction.toLowerCase().includes('climb') ? 'Climb' : 'Conventional';
    
    // Get values in display units
    const displayFeedRate = parseFloat(getResultValue(result.feed_rate, 2));
    const displayPlungeRate = parseFloat(getResultValue(result.plunge_rate, 2));
    const displayRampPlungeRate = parseFloat(getResultValue(result.ramp_plunge_rate, 2));
    const displayPassDepth = parseFloat(getResultValue(result.pass_depth, 4));
    const displayStepoverRough = parseFloat(getResultValue(result.stepover_rough, 4));
    const displayStepoverFinish = parseFloat(getResultValue(result.stepover_finish, 4));
    
    // Estimate flute length based on tool diameter (typically 2-3x diameter)
    const fluteLength = formData.tool_diameter * 2.5;
    
    const toolLibrary = {
      version: 1,
      tools: [
        {
          description: `${toolName} | CNC Router`,
          vendor: "CNCHelpdesk",
          "product-id": productId,
          type: "flat end mill",
          unit: unitSystem === 'inch' ? 'inches' : 'millimeters',
          geometry: {
            diameter: formData.tool_diameter,
            numberOfFlutes: formData.flute_count,
            shaftDiameter: formData.tool_diameter,
            shoulderLength: fluteLength * 1.5,
            fluteLength: fluteLength,
            overallLength: fluteLength * 2
          },
          cuttingData: {
            spindleSpeed: result.spindle_rpm,
            feedrate: displayFeedRate,
            plungeFeedrate: displayPlungeRate,
            rampFeedrate: displayRampPlungeRate,
            maximumStepdown: displayPassDepth,
            optimalLoad: displayStepoverRough,
            finishStep: displayStepoverFinish
          },
          coolant: "disabled",
          notes: `Generated by CNC-Helpdesk\n\n*** IMPORTANT: ALL VALUES ARE IN ${unitSystem === 'inch' ? 'INCHES (in/min for feeds)' : 'MILLIMETERS (mm/min for feeds)'} ***\n\nMaterial: ${materialName}\nChipload: ${result.chip_load.toFixed(4)} ${unitLabel}/tooth\nSpindle: ${spindleName}\nCutting direction: ${cutDirection}\nBit Direction: ${formData.bit_direction}`
        }
      ]
    };
    
    return JSON.stringify(toolLibrary, null, 2);
  };

  // Generate Carbide Create CSV file (beginner-friendly)
  const generateCarbideCreateCSV = () => {
    const materialName = getMaterialName();
    const unitLabel = unitSystem === 'inch' ? 'in' : 'mm';
    const feedUnitLabel = unitSystem === 'inch' ? 'in/min' : 'mm/min';
    const toolName = `CNCHelpdesk | ${materialName} | ${formData.tool_diameter}${unitLabel} | ${formData.flute_count}F`;
    
    const csvRows = [
      ['Parameter', 'Value'],
      ['', ''],
      ['*** IMPORTANT ***', `ALL VALUES ARE IN ${unitSystem === 'inch' ? 'INCHES (in/min for feeds)' : 'MILLIMETERS (mm/min for feeds)'}`],
      ['', ''],
      ['Tool Name', toolName],
      ['Tool Type', 'End Mill (Flat)'],
      ['Material', materialName],
      [`Tool Diameter (${unitLabel})`, formData.tool_diameter],
      ['Number of Flutes', formData.flute_count],
      ['Spindle RPM', result.spindle_rpm],
      [`Feed Rate (${feedUnitLabel})`, getResultValue(result.feed_rate, 2)],
      [`Plunge Rate (${feedUnitLabel})`, getResultValue(result.plunge_rate, 2)],
      [`Ramp Plunge Rate (${feedUnitLabel})`, getResultValue(result.ramp_plunge_rate, 2)],
      [`Pass Depth Recommended (${unitLabel})`, getResultValue(result.recommended_pass_depth_mm || result.pass_depth, 4)],
      [`Pass Depth Max Safe (${unitLabel})`, getResultValue(result.max_pass_depth_mm || result.pass_depth_max, 4)],
      [`Roughing Stepover (${unitLabel})`, getResultValue(result.stepover_rough, 4)],
      [`Finishing Stepover (${unitLabel})`, getResultValue(result.stepover_finish, 4)],
      ['Cutting Direction', 'Climb'],
      ['Notes', 'Generated by CNC-Helpdesk'],
    ];
    
    return csvRows.map(row => row.join(',')).join('\n');
  };

  // Handle export button click - show modal
  const handleExportClick = () => {
    setShowExportModal(true);
  };

  // Get current export target info
  const getCurrentExportTarget = () => {
    return exportTargets.find(t => t.value === exportTarget) || exportTargets[0];
  };

  // Get filename based on export target
  const getExportFilename = () => {
    const materialName = getMaterialName().replace(/\s+/g, '');
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

    if (exportTarget === 'fusion360') {
      return `CNCHelpdesk_${materialName}_${formData.tool_diameter}mm_${formData.flute_count}F_${stamp}_Fusion360.json`;
    } else if (exportTarget === 'carbide') {
      return `CNCHelpdesk_${materialName}_${formData.tool_diameter}mm_${formData.flute_count}F_${stamp}_CarbideCreate.csv`;
    }
    return `CNCHelpdesk_${materialName}_${formData.tool_diameter}mm_${formData.flute_count}F_${stamp}.TOOL`;
  };

  // Handle confirmed download
  const handleConfirmedDownload = async () => {
    let content, mimeType;
    
    if (exportTarget === 'fusion360') {
      content = generateFusion360JSON();
      mimeType = 'application/json';
    } else if (exportTarget === 'carbide') {
      content = generateCarbideCreateCSV();
      mimeType = 'text/csv';
    } else {
      // Aspire: request patched .tools binary from backend template
      const materialName = getMaterialName();
      const toolName = `CNCHelpdesk | ${materialName} | ${formData.tool_diameter}mm | ${formData.flute_count}F`;
      const aspirePayload = {
        name: toolName,
        diameter_mm: formData.tool_diameter,
        flute_count: formData.flute_count,
        feed_xy: result.feed_rate,
        plunge: result.plunge_rate,
        rpm: result.spindle_rpm,
        stepover_rough_mm: result.stepover_rough,
      };

      // Synchronous await for backend binary
      const res = await fetch(`${API}/calculators/aspire-tools`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(aspirePayload),
      });

      if (!res.ok) {
        toast.error('Failed to generate Aspire tool file.');
        setShowExportModal(false);
        return;
      }

      const arrayBuffer = await res.arrayBuffer();
      content = new Uint8Array(arrayBuffer);
      mimeType = 'application/octet-stream';
    }
    
    const fileName = getExportFilename();
    
    // Create blob and download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setShowExportModal(false);
    toast.success(`Tool file "${fileName}" downloaded successfully!`);
  };

  return (
    <div 
      data-testid="calculator-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.ROUTER_CALCULATOR)}
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Job Setup</div>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#e67e22] tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Cutting Parameters
            </h1>
            <p className="mt-2 text-sm md:text-base text-[#e67e22] max-w-lg">
              Machine-first inputs, CNC-style readouts, and safety guidance.
            </p>
          </div>
          <div className="hidden md:block">
            <div className="bg-black/50 backdrop-blur-sm px-4 py-2 rounded-sm border border-[#3a3a3a]">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${riskDotClass(risks.burn)}`} />
                <span className="text-[#a3a3a3] text-sm">Confidence:</span>
                <span className="text-white text-sm font-medium">{confidenceLabel(risks.confidence)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Resume Last Job */}
        {resumeData?.formData && (
          <div className="flex items-center justify-between p-3 mb-4 rounded-sm border border-[#e67e22]/40 bg-[#e67e22]/10">
            <div className="text-sm text-[#e67e22]">
              Resume last job: <span className="font-semibold">{resumeData.formData.material?.replace(/_/g, ' ')}</span>
            </div>
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, ...resumeData.formData }));
                if (resumeData.unitSystem) setUnitSystem(resumeData.unitSystem);
                setResumeData(null);
                localStorage.removeItem('cnc:resume_calc');
                toast.success('Last job restored');
              }}
              className="text-xs px-3 py-1 rounded border border-[#e67e22] text-[#e67e22] hover:bg-[#e67e22] hover:text-black transition-colors"
            >
              Restore
            </button>
          </div>
        )}

        {/* Quick-Start Presets */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Rout Hardwood Pocket', sub: 'Oak · 6.35mm upcut · medium', preset: { material: 'hardwood_oak', tool_type: 'end_mill', bit_direction: 'upcut', machine_rigidity: 'medium', flute_count: 2, tool_diameter: 6.35, cutting_diameter: 6.35, shank_diameter: 6.35, spindle_type: 'medium_spindle' } },
            { label: 'Engrave Acrylic', sub: 'Acrylic · V-bit · light', preset: { material: 'plastic_acrylic', tool_type: 'v_bit', bit_direction: 'downcut', machine_rigidity: 'light', flute_count: 1, tool_diameter: 6.35, cutting_diameter: 6.35, shank_diameter: 6.35, spindle_type: 'medium_spindle' } },
            { label: 'Cut MDF Sheet', sub: 'MDF · 6.35mm downcut · medium', preset: { material: 'sheet_mdf', tool_type: 'end_mill', bit_direction: 'downcut', machine_rigidity: 'medium', flute_count: 2, tool_diameter: 6.35, cutting_diameter: 6.35, shank_diameter: 6.35, spindle_type: 'medium_spindle' } },
            { label: 'Drill Aluminium', sub: 'Aluminium · 6.35mm · heavy', preset: { material: 'metal_aluminum', tool_type: 'end_mill', bit_direction: 'upcut', machine_rigidity: 'heavy', flute_count: 2, tool_diameter: 6.35, cutting_diameter: 6.35, shank_diameter: 6.35, spindle_type: 'high_speed_spindle' } },
          ].map(({ label, sub, preset }) => (
            <button
              key={label}
              type="button"
              onClick={() => { setFormData(prev => ({ ...prev, ...preset })); toast.success('Preset loaded — adjust as needed'); }}
              className="text-left p-3 rounded-sm border border-[#3a3a3a] bg-black/50 hover:border-[#e67e22] hover:bg-[#e67e22]/10 transition-colors group"
            >
              <div className="text-sm font-semibold text-white group-hover:text-[#e67e22]">{label}</div>
              <div className="text-xs text-[#a3a3a3] mt-0.5">{sub}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
          <CardHeader>
            <CardTitle className="text-[#292524] flex items-center gap-2">
              <CalcIcon className="w-6 h-6 text-[#B45309]" />
              Job Setup Panels
            </CardTitle>
            <CardDescription className="text-[#78716C]">Machine, tool, material, and strategy inputs</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCalculate} className="space-y-4">
              {/* MACHINE */}
              <div className="calc-panel">
                <div className="calc-panel-title">Machine</div>
                
                {/* Machine Profile Selector */}
                {user && userMachines.length > 0 && (
                  <div className="space-y-2 mb-4 p-3 rounded-lg bg-[#B45309]/10 border border-[#B45309]/30">
                    <Label className="text-xs font-bold uppercase text-[#B45309] flex items-center gap-1">
                      <Settings2 className="w-3 h-3" />
                      Use Saved Machine Profile
                    </Label>
                    <Select
                      value={selectedMachineId}
                      onValueChange={applyMachineProfile}
                    >
                      <SelectTrigger data-testid="machine-profile-select" className="bg-white/60 border-[#B45309]/50 text-[#292524]">
                        <SelectValue placeholder="Select a machine to auto-fill settings" />
                      </SelectTrigger>
                      <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                        {userMachines.map((machine) => (
                          <SelectItem key={machine.id} value={machine.id}>
                            <div className="flex flex-col py-1">
                              <span className="font-semibold">{machine.name || 'Unnamed Machine'}</span>
                              <span className="text-xs text-[#78716C]">
                                {machine.spindle_type_manual || machine.spindle_type_inferred || 'Unknown spindle'} • {machine.rigidity || 'Unknown'} rigidity
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedMachineName && (
                      <p className="text-xs text-[#B45309]">
                        Using profile: <span className="font-semibold">{selectedMachineName}</span>
                      </p>
                    )}
                    <p className="text-xs text-[#78716C]">
                      Settings like spindle type and rigidity will be pre-filled from your machine profile.
                    </p>
                  </div>
                )}

                {user && userMachines.length === 0 && !loadingMachines && (
                  <div className="mb-4 p-3 rounded-lg bg-slate-100 border border-slate-200">
                    <p className="text-xs text-[#78716C]">
                      <span className="font-semibold text-[#292524]">Tip:</span> Set up a machine profile in{' '}
                      <a href="/machine-setup" className="text-[#B45309] underline hover:text-[#B45309]/80">
                        Machine Setup
                      </a>{' '}
                      to auto-fill these settings.
                    </p>
                  </div>
                )}

                {/* Unit System Selector - FIRST */}
                <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-[#78716C]">Unit System</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => setUnitSystem('mm')}
                    className={`flex-1 ${
                      unitSystem === 'mm' 
                        ? 'bg-[#B45309] text-[#0F172A] font-bold' 
                        : 'bg-white/60 text-[#78716C] border border-[#D6D3D1] hover:bg-[#334155]'
                    }`}
                  >
                    Metric (mm)
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setUnitSystem('inch')}
                    className={`flex-1 ${
                      unitSystem === 'inch' 
                        ? 'bg-[#B45309] text-[#0F172A] font-bold' 
                        : 'bg-white/60 text-[#78716C] border border-[#D6D3D1] hover:bg-[#334155]'
                    }`}
                  >
                    Imperial (inch)
                  </Button>
                </div>
                <p className="text-xs text-[#78716C]">
                  All inputs and outputs will use {unitSystem === 'mm' ? 'millimeters (mm)' : 'inches (in)'}
                </p>
                </div>

                {/* Spindle Type */}
                <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-[#78716C]">Spindle Type</Label>
                <Select
                  value={formData.spindle_type}
                  onValueChange={(value) => setFormData({ ...formData, spindle_type: value })}
                >
                  <SelectTrigger data-testid="spindle-type-select" className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1] max-w-[400px]">
                    {spindleTypes.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        <div className="flex flex-col py-1">
                          <span className="font-semibold">{s.label}</span>
                          <span className="text-xs text-[#B45309]">{s.power} • {s.rpm}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.spindle_type && (
                  <p className="text-xs text-[#78716C] mt-1">
                    {spindleTypes.find(s => s.value === formData.spindle_type)?.description}
                  </p>
                )}
                </div>

                {/* Rigidity */}
                <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-[#78716C]">Machine Rigidity</Label>
                <Select
                  value={formData.machine_rigidity}
                  onValueChange={(value) => setFormData({ ...formData, machine_rigidity: value })}
                >
                  <SelectTrigger className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                    {machineRigidityOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        <div className="flex flex-col py-1">
                          <span className="font-semibold">{o.label}</span>
                          <span className="text-xs text-[#78716C]">{o.hint}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>

                {/* Tool sharpness */}
                <div className="space-y-2">
                <Label className="text-xs font-bold uppercase text-[#78716C]">Tool Sharpness</Label>
                <Select
                  value={formData.tool_sharpness}
                  onValueChange={(value) => setFormData({ ...formData, tool_sharpness: value })}
                >
                  <SelectTrigger className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                    {toolSharpnessOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        <div className="flex flex-col py-1">
                          <span className="font-semibold">{o.label}</span>
                          <span className="text-xs text-[#78716C]">{o.hint}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>

                {/* End MACHINE */}
              </div>

              {/* MATERIAL */}
              <div className="calc-panel">
                <div className="calc-panel-title">Material & Tool</div>

                {/* User Imported Tools Selector */}
                {user && userTools.length > 0 && (
                  <div className="space-y-2 mb-4 p-3 rounded-lg bg-[#e67e22]/10 border border-[#e67e22]/30">
                    <Label className="text-xs font-bold uppercase text-[#e67e22] flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      {toolSource === 'combined' 
                        ? 'Quick Select from Your Imported Tools + Default Library' 
                        : `Quick Select from Tool Library (${userTools.length} tools)`}
                    </Label>
                    <Select
                      value={selectedUserToolId}
                      onValueChange={applyUserTool}
                    >
                      <SelectTrigger data-testid="user-tool-select" className="bg-white/60 border-[#e67e22]/50 text-[#292524]">
                        <SelectValue placeholder={`Select from ${userTools.length} tools...`} />
                      </SelectTrigger>
                      <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1] max-h-[400px]">
                        <SelectGroup>
                          <SelectLabel className="text-[#e67e22] font-bold">End Mills ({userTools.filter(t => t.category === 'End Mill').length})</SelectLabel>
                          {userTools.filter(t => t.category === 'End Mill').map((tool) => (
                            <SelectItem key={tool.id} value={tool.id}>
                              <div className="flex flex-col py-1">
                                <span className="font-semibold text-sm">{tool.name}</span>
                                <span className="text-xs text-[#78716C]">
                                  Cut: Ø{tool.cutting_diameter_mm}mm • Shank: {tool.shank_diameter_mm}mm • {tool.flute_count}F
                                  {tool.subcategory && ` • ${tool.subcategory}`}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[#e67e22] font-bold">Ball Nose ({userTools.filter(t => t.category === 'Ball Nose' || t.category === 'Tapered Ball Nose').length})</SelectLabel>
                          {userTools.filter(t => t.category === 'Ball Nose' || t.category === 'Tapered Ball Nose').map((tool) => (
                            <SelectItem key={tool.id} value={tool.id}>
                              <div className="flex flex-col py-1">
                                <span className="font-semibold text-sm">{tool.name}</span>
                                <span className="text-xs text-[#78716C]">
                                  Cut: Ø{tool.cutting_diameter_mm}mm • {tool.flute_count}F
                                  {tool.taper_angle && ` • ${tool.taper_angle}° taper`}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[#e67e22] font-bold">V-Bits ({userTools.filter(t => t.category === 'V-Bit').length})</SelectLabel>
                          {userTools.filter(t => t.category === 'V-Bit').map((tool) => (
                            <SelectItem key={tool.id} value={tool.id}>
                              <div className="flex flex-col py-1">
                                <span className="font-semibold text-sm">{tool.name}</span>
                                <span className="text-xs text-[#78716C]">
                                  {tool.v_angle}° • Shank: {tool.shank_diameter_mm}mm
                                  {tool.tip_diameter_mm && ` • Tip: ${tool.tip_diameter_mm}mm`}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[#e67e22] font-bold">Engraving ({userTools.filter(t => t.category === 'Engraving').length})</SelectLabel>
                          {userTools.filter(t => t.category === 'Engraving').map((tool) => (
                            <SelectItem key={tool.id} value={tool.id}>
                              <div className="flex flex-col py-1">
                                <span className="font-semibold text-sm">{tool.name}</span>
                                <span className="text-xs text-[#78716C]">
                                  {tool.v_angle}° • Tip: {tool.tip_diameter_mm}mm
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[#e67e22] font-bold">Surfacing ({userTools.filter(t => t.category === 'Surfacing').length})</SelectLabel>
                          {userTools.filter(t => t.category === 'Surfacing').map((tool) => (
                            <SelectItem key={tool.id} value={tool.id}>
                              <div className="flex flex-col py-1">
                                <span className="font-semibold text-sm">{tool.name}</span>
                                <span className="text-xs text-[#78716C]">
                                  Cut: Ø{tool.cutting_diameter_mm}mm × {tool.cutting_length_mm}mm deep • Shank: {tool.shank_diameter_mm}mm
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="text-[#e67e22] font-bold">Drills & Specialty ({userTools.filter(t => t.category === 'Drill' || !['End Mill', 'Ball Nose', 'Tapered Ball Nose', 'V-Bit', 'Engraving', 'Surfacing'].includes(t.category)).length})</SelectLabel>
                          {userTools.filter(t => t.category === 'Drill' || !['End Mill', 'Ball Nose', 'Tapered Ball Nose', 'V-Bit', 'Engraving', 'Surfacing'].includes(t.category)).map((tool) => (
                            <SelectItem key={tool.id} value={tool.id}>
                              <div className="flex flex-col py-1">
                                <span className="font-semibold text-sm">{tool.name}</span>
                                <span className="text-xs text-[#78716C]">
                                  Ø{tool.cutting_diameter_mm}mm • {tool.flute_count}F • {tool.category}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {selectedUserToolName && (
                      <p className="text-xs text-[#e67e22]">
                        Using: <span className="font-semibold">{selectedUserToolName}</span>
                      </p>
                    )}
                    {toolSource === 'application_database' && (
                      <p className="text-xs text-[#78716C] mt-1">
                        💡 Import your own tools from Aspire/VCarve to add them to this list
                      </p>
                    )}
                    {toolSource === 'combined' && (
                      <p className="text-xs text-[#27ae60] mt-1">
                        ✓ Showing your imported tools + default library
                      </p>
                    )}
                  </div>
                )}

                {/* Import Tools Section - Always show */}
                {user && (
                  <div className="mb-4 p-3 rounded-lg bg-slate-100 border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold text-[#292524] uppercase tracking-wide mb-0.5">
                          Import Your Tool Database
                        </p>
                        <p className="text-xs text-[#78716C]">
                          From Vectric Aspire/VCarve or Fusion 360
                        </p>
                      </div>
                      <a 
                        href="/knowledge/tools" 
                        className="text-xs font-bold text-[#B45309] hover:text-[#B45309]/80 underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Import Tools
                      </a>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-[#78716C]">Material</Label>
                  <Select
                    value={formData.material}
                    onValueChange={(value) => setFormData({ ...formData, material: value })}
                  >
                    <SelectTrigger data-testid="material-select" className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1] max-h-[300px]">
                      {materialCategories.map((category) => (
                        <SelectGroup key={category.label}>
                          <SelectLabel className="text-[#B45309] font-bold">{category.label}</SelectLabel>
                          {category.materials.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-[#78716C]">Bit Type</Label>
                  <Select
                    value={formData.tool_type}
                    onValueChange={(value) => setFormData({ ...formData, tool_type: value })}
                  >
                    <SelectTrigger data-testid="tool-type-select" className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                      {toolTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-[#78716C]">Bit Direction</Label>
                  <Select
                    value={formData.bit_direction}
                    onValueChange={(value) => setFormData({ ...formData, bit_direction: value })}
                  >
                    <SelectTrigger data-testid="bit-direction-select" className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                      {bitDirections.map((b) => (
                        <SelectItem key={b.value} value={b.value}>
                          <div className="flex flex-col">
                            <span>{b.label}</span>
                            <span className="text-xs text-[#78716C]">{b.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-[#78716C]">Cutting Diameter ({unitSystem === 'inch' ? 'inches' : 'mm'})</Label>
                  <Input
                    data-testid="diameter-input"
                    type="number"
                    step={unitSystem === 'inch' ? '0.001' : '0.01'}
                    value={formData.cutting_diameter}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      setFormData({ ...formData, cutting_diameter: val, tool_diameter: val });
                    }}
                    className="bg-white/60 border-[#D6D3D1] text-[#292524] font-mono"
                    placeholder={unitSystem === 'inch' ? 'e.g., 0.25' : 'e.g., 6.35'}
                    required
                  />
                  <p className="text-xs text-[#78716C]">Actual cutting width of the tool</p>
                </div>

                {/* Advanced Tool Geometry - Collapsible */}
                <div className="space-y-3 p-3 rounded-lg bg-[#78716C]/5 border border-[#78716C]/20">
                  <Label className="text-xs font-bold uppercase text-[#e67e22] flex items-center gap-1">
                    <Settings2 className="w-3 h-3" />
                    Tool Geometry (affects calculations)
                  </Label>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-[#78716C]">Shank Ø ({unitSystem === 'inch' ? 'in' : 'mm'})</Label>
                      <Input
                        data-testid="shank-diameter-input"
                        type="number"
                        step={unitSystem === 'inch' ? '0.001' : '0.01'}
                        value={formData.shank_diameter}
                        onChange={(e) => setFormData({ ...formData, shank_diameter: parseFloat(e.target.value) })}
                        className="bg-white/60 border-[#D6D3D1] text-[#292524] font-mono text-sm"
                        placeholder={unitSystem === 'inch' ? '0.25' : '6.35'}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-[#78716C]">Cutting Length ({unitSystem === 'inch' ? 'in' : 'mm'})</Label>
                      <Input
                        data-testid="cutting-length-input"
                        type="number"
                        step={unitSystem === 'inch' ? '0.01' : '0.1'}
                        value={formData.cutting_length}
                        onChange={(e) => setFormData({ ...formData, cutting_length: parseFloat(e.target.value) })}
                        className="bg-white/60 border-[#D6D3D1] text-[#292524] font-mono text-sm"
                        placeholder={unitSystem === 'inch' ? '0.87' : '22'}
                      />
                    </div>
                  </div>
                  
                  {formData.tool_type === 'v_bit' && (
                    <div className="space-y-1">
                      <Label className="text-xs text-[#78716C]">V-Bit Angle (degrees)</Label>
                      <Input
                        data-testid="v-angle-input"
                        type="number"
                        step="1"
                        value={formData.v_angle || ''}
                        onChange={(e) => setFormData({ ...formData, v_angle: parseFloat(e.target.value) || null })}
                        className="bg-white/60 border-[#D6D3D1] text-[#292524] font-mono text-sm"
                        placeholder="e.g., 60"
                      />
                    </div>
                  )}
                  
                  <p className="text-xs text-[#78716C] italic">
                    💡 Shank ≠ Cutting diameter for surfacing bits, V-bits, etc.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-[#78716C]">Flute Count</Label>
                  <Input
                    data-testid="flute-input"
                    type="number"
                    value={formData.flute_count}
                    onChange={(e) => setFormData({ ...formData, flute_count: parseInt(e.target.value) })}
                    className="bg-white/60 border-[#D6D3D1] text-[#292524] font-mono"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-[#78716C]">Spindle RPM (Optional - auto-calculated if empty)</Label>
                  <Input
                    data-testid="rpm-input"
                    type="number"
                    value={formData.spindle_rpm}
                    onChange={(e) => setFormData({ ...formData, spindle_rpm: e.target.value ? parseInt(e.target.value) : '' })}
                    className="bg-white/60 border-[#D6D3D1] text-[#292524] font-mono"
                    placeholder="Auto-detect from spindle & material"
                  />
                </div>

                {/* End MATERIAL */}
              </div>

              <Button
                data-testid="calculate-btn"
                type="submit"
                className="w-full bg-[#B45309] hover:bg-[#B45309]/90 text-[#0F172A] font-bold uppercase tracking-wide"
                disabled={isLoading}
              >
                {isLoading ? 'Calculating...' : 'Calculate'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {result && (
          <Card className="bg-gradient-to-br from-[#1E293B] to-white/60/50 border-[#F59E0B]/50">
            <CardHeader>
              <CardTitle className="text-[#292524]">Aspire-Ready Profile</CardTitle>
              <CardDescription className="text-[#78716C]">
                Complete cutting parameters for your CAM software
                <span className="ml-2 px-2 py-1 bg-[#B45309]/20 rounded text-[#B45309] text-xs font-bold">
                  {unitSystem === 'inch' ? 'IMPERIAL (inches)' : 'METRIC (mm)'}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent data-testid="calculator-results" className="space-y-6">
              {/* Safety & primary parameters */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Safety & risk band */}
                <div className="space-y-3">
                  <div className="readout-row">
                    <div>
                      <div className="readout-label">Overall band</div>
                      <div className="flex items-center gap-2 mt-1">
                        <div className={`risk-dot ${riskDotClass(risks.burn)} status-dot`} />
                        <span className="text-sm text-[var(--text-primary)] font-medium">
                          {confidenceLabel(risks.confidence)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="readout-row">
                    <div className="readout-label">Burn risk</div>
                    <div className="flex items-center gap-2">
                      <div className={`risk-dot ${riskDotClass(risks.burn)} status-dot`} />
                      <span className="text-xs text-[var(--text-secondary)] capitalize">{risks.burn}</span>
                    </div>
                  </div>

                  <div className="readout-row">
                    <div className="readout-label">Chatter risk</div>
                    <div className="flex items-center gap-2">
                      <div className={`risk-dot ${riskDotClass(risks.chatter)} status-dot`} />
                      <span className="text-xs text-[var(--text-secondary)] capitalize">{risks.chatter}</span>
                    </div>
                  </div>

                  <div className="readout-row">
                    <div className="readout-label">Tear-out risk</div>
                    <div className="flex items-center gap-2">
                      <div className={`risk-dot ${riskDotClass(risks.tearout)} status-dot`} />
                      <span className="text-xs text-[var(--text-secondary)] capitalize">{risks.tearout}</span>
                    </div>
                  </div>

                  {(risks.reasons?.length || 0) > 0 && (
                    <div className="text-xs text-[var(--text-muted)] space-y-1">
                      {risks.reasons.slice(0, 2).map((r, idx) => (
                        <div key={idx}>
                          • {r}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Primary Parameters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white/60 p-4 rounded-lg border border-[#D6D3D1]">
                    <div className="text-xs font-bold uppercase text-[#78716C] mb-1">Spindle RPM</div>
                    <div className="text-xl font-mono font-bold text-[#B45309] break-words">{result.spindle_rpm}</div>
                  </div>
                  <div className="bg-white/60 p-4 rounded-lg border border-[#D6D3D1]">
                    <div className="text-xs font-bold uppercase text-[#78716C] mb-1">Feed Rate (XY)</div>
                    <div className="text-xl font-mono font-bold text-[#10B981] break-words">{getResultValue(result.feed_rate, 1)}</div>
                    <div className="text-xs text-[#78716C]">{getUnitLabel('feedrate')}</div>
                  </div>
                  <div className="bg-white/60 p-4 rounded-lg border border-[#D6D3D1]">
                    <div className="text-xs font-bold uppercase text-[#78716C] mb-1">Plunge Rate (Z)</div>
                    <div className="text-xl font-mono font-bold text-blue-400 break-words">{getResultValue(result.plunge_rate, 1)}</div>
                    <div className="text-xs text-[#78716C]">{getUnitLabel('feedrate')}</div>
                  </div>
                  <div className="bg-white/60 p-4 rounded-lg border border-[#D6D3D1]">
                    <div className="text-xs font-bold uppercase text-[#78716C] mb-1">Ramp Plunge Rate</div>
                    <div className="text-xl font-mono font-bold text-cyan-400 break-words">{getResultValue(result.ramp_plunge_rate, 1)}</div>
                    <div className="text-xs text-[#78716C]">{getUnitLabel('feedrate')}</div>
                  </div>
                  <div className="bg-white/60 p-4 rounded-lg border border-[#D6D3D1]">
                    <div className="text-xs font-bold uppercase text-[#78716C] mb-1">Pass Depth</div>
                    <div className="space-y-2">
                      <div>
                        <div className="text-[10px] uppercase text-[#78716C] tracking-wide">Recommended</div>
                        <div className="text-xl font-mono font-bold text-purple-400 break-words">{getResultValue(result.recommended_pass_depth_mm || result.pass_depth, 3)} <span className="text-xs text-[#78716C]">{getUnitLabel('length')}</span></div>
                      </div>
                      <div className="border-t border-[#D6D3D1] pt-2">
                        <div className="text-[10px] uppercase text-[#78716C] tracking-wide">Max Safe</div>
                        <div className="text-lg font-mono font-bold text-orange-500 break-words">{getResultValue(result.max_pass_depth_mm || result.pass_depth_max, 3)} <span className="text-xs text-[#78716C]">{getUnitLabel('length')}</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/60 p-4 rounded-lg border border-[#D6D3D1]">
                    <div className="text-xs font-bold uppercase text-[#78716C] mb-1">Ramp Angle</div>
                    <div className="text-xl font-mono font-bold text-pink-400 break-words">{result.ramp_angle}°</div>
                  </div>
                </div>
              </div>

              {/* Stepover Parameters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/60 p-4 rounded-lg border border-[#F59E0B]/30">
                  <div className="text-xs font-bold uppercase text-[#B45309] mb-2">Stepover (Roughing)</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-mono font-bold text-[#292524]">{getResultValue(result.stepover_rough, 3)}</span>
                    <span className="text-sm text-[#78716C]">{getUnitLabel('length')}</span>
                    <span className="text-lg font-mono text-[#B45309]">({result.stepover_rough_percent}%)</span>
                  </div>
                </div>
                <div className="bg-white/60 p-4 rounded-lg border border-[#10B981]/30">
                  <div className="text-xs font-bold uppercase text-[#10B981] mb-2">Stepover (Finishing)</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-mono font-bold text-[#292524]">{getResultValue(result.stepover_finish, 3)}</span>
                    <span className="text-sm text-[#78716C]">{getUnitLabel('length')}</span>
                    <span className="text-lg font-mono text-[#10B981]">({result.stepover_finish_percent}%)</span>
                  </div>
                </div>
              </div>

              {/* Cutting Direction */}
              <div className="bg-white/60 p-4 rounded-lg border border-[#D6D3D1]">
                <div className="text-xs font-bold uppercase text-[#78716C] mb-2">Cutting Direction</div>
                <div className="text-lg text-[#292524]">{result.cutting_direction}</div>
              </div>

              {result.recommendations && result.recommendations.length > 0 && (
                <div className="bg-[#10B981]/10 border border-[#10B981]/50 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 text-[#10B981] mt-0.5" />
                    <div className="space-y-1">
                      <div className="text-sm font-bold text-[#10B981]">Recommendations</div>
                      {result.recommendations.map((rec, index) => (
                        <div key={index} className="text-sm text-[#57534E]">
                          • {rec}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {result.warnings && result.warnings.length > 0 && (
                <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div className="space-y-1">
                      <div className="text-sm font-bold text-red-400">Warnings</div>
                      {result.warnings.map((warning, index) => (
                        <div key={index} className="text-sm text-[#57534E]">
                          • {warning}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Contextual Troubleshooting Links */}
              {(() => {
                const troubleshootingLinks = [];
                const mat = String(formData.material).toLowerCase();
                const isWood = mat.includes('softwood') || mat.includes('hardwood') || mat.includes('exotic') || mat.includes('sheet');
                const isMetal = mat.includes('metal') || mat.includes('aluminum') || mat.includes('brass') || mat.includes('copper') || mat.includes('steel');
                const isPlastic = mat.includes('plastic') || mat.includes('acrylic') || mat.includes('hdpe') || mat.includes('pvc') || mat.includes('abs');
                const isFoam = mat.includes('foam');
                
                // Add contextual links based on material and risk levels
                if (risks.burn === 'high' || risks.burn === 'medium') {
                  troubleshootingLinks.push({
                    title: 'Burning / Scorching',
                    desc: 'Feed rate or RPM may need adjustment',
                    category: 'surface_quality'
                  });
                }
                
                if (risks.chatter === 'high' || risks.chatter === 'medium') {
                  troubleshootingLinks.push({
                    title: 'Chatter / Vibration',
                    desc: 'Check stepover, RPM, and tool stickout',
                    category: 'motion_stability'
                  });
                }
                
                if (risks.tearout === 'high' || risks.tearout === 'medium') {
                  troubleshootingLinks.push({
                    title: 'Tear-out',
                    desc: 'Consider downcut or compression bit',
                    category: 'surface_quality'
                  });
                }
                
                if (isPlastic) {
                  troubleshootingLinks.push({
                    title: 'Plastic Melting',
                    desc: 'RPM and chip evacuation tips',
                    category: 'material_specific'
                  });
                }
                
                if (isMetal && mat.includes('aluminum')) {
                  troubleshootingLinks.push({
                    title: 'Aluminum Chip Welding',
                    desc: 'Lubrication and coating tips',
                    category: 'material_specific'
                  });
                }
                
                if (formData.tool_diameter < 4) {
                  troubleshootingLinks.push({
                    title: 'Bit Breakage',
                    desc: 'Small tool protection tips',
                    category: 'tool_issues'
                  });
                }
                
                if (troubleshootingLinks.length === 0 && isWood) {
                  // Default helpful link for wood users
                  troubleshootingLinks.push({
                    title: 'Poor Surface Finish',
                    desc: 'General finish improvement tips',
                    category: 'surface_quality'
                  });
                }
                
                return troubleshootingLinks.length > 0 ? (
                  <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <Lightbulb className="w-5 h-5 text-blue-400 mt-0.5" />
                      <div>
                        <div className="text-sm font-bold text-blue-400">Troubleshooting Resources</div>
                        <div className="text-xs text-[#78716C]">Based on your material and settings</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {troubleshootingLinks.slice(0, 4).map((link, index) => (
                        <a
                          key={index}
                          href={`/troubleshooting?category=${link.category}`}
                          className="flex items-center gap-2 p-2 bg-blue-900/30 rounded hover:bg-blue-900/50 transition-colors group"
                        >
                          <ExternalLink className="w-4 h-4 text-blue-400 group-hover:text-blue-300" />
                          <div>
                            <div className="text-sm text-blue-300 font-medium">{link.title}</div>
                            <div className="text-xs text-[#78716C]">{link.desc}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Export Section */}
              <div className="pt-4 border-t border-[#D6D3D1] space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase text-[#78716C]">Export Target Application</Label>
                  <Select
                    value={exportTarget}
                    onValueChange={setExportTarget}
                  >
                    <SelectTrigger className="bg-white/60 border-[#D6D3D1] text-[#292524]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
                      {exportTargets.map((target) => (
                        <SelectItem key={target.value} value={target.value}>
                          <div className="flex items-center gap-2">
                            <span>{target.label}</span>
                            <span className="text-xs text-[#78716C]">({target.extension})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button
                  onClick={handleExportClick}
                  className="w-full bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-[#292524] font-bold py-3 text-lg"
                >
                  <FileDown className="w-5 h-5 mr-2" />
                  Export to {getCurrentExportTarget().label}
                </Button>
                <p className="text-xs text-[#78716C] text-center">
                  Download as {getCurrentExportTarget().label} {getCurrentExportTarget().extension} file
                </p>
                
                {/* Test Before Cutting Button */}
                <Button
                  onClick={() => setShowTestCutModal(true)}
                  variant="outline"
                  className="w-full mt-2 border-orange-500 text-orange-500 hover:bg-orange-500/10"
                >
                  <FlaskConical className="w-5 h-5 mr-2" />
                  Generate Test Cut
                </Button>
                <p className="text-xs text-[#78716C] text-center">
                  Validate settings before committing to expensive material
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      </div>

      {/* ===================== */}
      {/* JOB COSTING CALCULATOR */}
      {/* ===================== */}
      <div className="max-w-6xl mx-auto mt-8">
        <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
          <CardHeader>
            <CardTitle className="text-[#292524] flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-[#B45309]" />
              Job Costing Calculator
            </CardTitle>
            <CardDescription className="text-[#78716C]">
              Estimate total job cost — updates live
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Currency */}
            <div>
              <div className="text-xs font-bold uppercase text-[#78716C] mb-2">Currency</div>
              <div className="grid grid-cols-4 gap-2">
                {['ZAR','USD','EUR','GBP'].map(c => (
                  <button key={c} onClick={() => handleCurrencyChange(c)}
                    className={`py-2 rounded-lg border text-sm font-bold transition-all
                      ${currency === c
                        ? 'bg-[#B45309] border-[#B45309] text-white'
                        : 'border-[#D6D3D1] text-[#78716C] hover:border-[#B45309] bg-white/60'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                {/* Duration */}
                <div>
                  <div className="text-xs font-bold uppercase text-[#78716C] mb-2">Job Duration</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Hours</label>
                      <input type="number" min="0" value={costInputs.jobHours}
                        onChange={e => setCost('jobHours', e.target.value)}
                        placeholder="0"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Minutes</label>
                      <input type="number" min="0" max="59" value={costInputs.jobMinutes}
                        onChange={e => setCost('jobMinutes', e.target.value)}
                        placeholder="0"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                  </div>
                </div>

                {/* Operating */}
                <div>
                  <div className="text-xs font-bold uppercase text-[#78716C] mb-2">Operating Costs</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Machine Rate ({sym}/hr)</label>
                      <input type="number" min="0" value={costInputs.machineRate}
                        onChange={e => setCost('machineRate', e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Electricity (Watts)</label>
                      <input type="number" min="0" value={costInputs.elecWatts}
                        onChange={e => setCost('elecWatts', e.target.value)}
                        placeholder="0"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Electricity Rate ({sym}/kWh)</label>
                      <input type="number" min="0" value={costInputs.elecRate}
                        onChange={e => setCost('elecRate', e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Material Cost ({sym})</label>
                      <input type="number" min="0" value={costInputs.materialCost}
                        onChange={e => setCost('materialCost', e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                {/* CNC specific */}
                <div>
                  <div className="text-xs font-bold uppercase text-[#78716C] mb-2">Tooling</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Bit Cost ({sym})</label>
                      <input type="number" min="0" value={costInputs.bitCost}
                        onChange={e => setCost('bitCost', e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Jobs per Bit</label>
                      <input type="number" min="1" value={costInputs.bitPasses}
                        onChange={e => setCost('bitPasses', e.target.value)}
                        placeholder="1"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                  </div>
                </div>

                {/* Overheads */}
                <div>
                  <div className="text-xs font-bold uppercase text-[#78716C] mb-2">Overheads &amp; Markup</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Wear &amp; Tear (%)</label>
                      <input type="number" min="0" max="100" value={costInputs.wearRate}
                        onChange={e => setCost('wearRate', e.target.value)}
                        placeholder="0"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[#78716C] text-sm">Markup (%)</label>
                      <input type="number" min="0" value={costInputs.markupPercent}
                        onChange={e => setCost('markupPercent', e.target.value)}
                        placeholder="0"
                        className="w-full bg-white/60 border border-[#D6D3D1] text-[#292524] rounded-md px-3 py-2 text-sm" />
                    </div>
                  </div>
                </div>

                {/* Results */}
                {costCalc ? (
                  <div className="p-4 bg-[#FEF3C7] border border-[#B45309]/30 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm text-[#57534E]">
                      <span>Machine cost</span>
                      <span>{sym}{costCalc.machineCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#57534E]">
                      <span>Electricity</span>
                      <span>{sym}{costCalc.elecCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#57534E]">
                      <span>Material</span>
                      <span>{sym}{costCalc.materialCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#57534E]">
                      <span>Bit wear (this job)</span>
                      <span>{sym}{costCalc.bitWear.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#57534E]">
                      <span>Wear &amp; tear</span>
                      <span>{sym}{costCalc.wearCost.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#78716C] border-t border-[#B45309]/20 pt-2">
                      <span>Subtotal</span>
                      <span>{sym}{costCalc.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#78716C]">
                      <span>Markup</span>
                      <span>+{sym}{costCalc.markupAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold text-[#B45309] border-t border-[#B45309]/30 pt-3">
                      <span>TOTAL JOB COST</span>
                      <span>{sym}{costCalc.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-[#78716C]">
                      <span>Cost per minute</span>
                      <span>{sym}{costCalc.perMinute.toFixed(2)}/min</span>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-white/40 border border-[#D6D3D1] rounded-lg text-center">
                    <p className="text-[#78716C] text-sm">Enter job duration to see cost breakdown</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="bg-white/80 backdrop-blur-sm border-[#D6D3D1] text-[#292524] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#B45309] flex items-center gap-2">
              <Download className="w-6 h-6" />
              How to Import This Tool into {getCurrentExportTarget().label}
            </DialogTitle>
            <DialogDescription className="text-[#78716C]">
              This file contains the tool settings generated by the CNC-Helpdesk calculator.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* IMPORTANT: Units notice - shown for ALL export types */}
            <div className="bg-yellow-900/30 border-2 border-yellow-500 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <div>
                  <p className="text-yellow-500 font-bold text-lg">IMPORTANT: UNITS</p>
                  <p className="text-yellow-400 font-semibold">
                    All values are in {unitSystem === 'inch' ? 'INCHES (in/min for feeds)' : 'MILLIMETERS (mm/min for feeds)'}
                  </p>
                </div>
              </div>
            </div>

            {/* Aspire Instructions */}
            {exportTarget === 'aspire' && (
              <>
                <div className="bg-white/60 rounded-lg p-4 border border-[#D6D3D1]">
                  <h4 className="font-bold text-[#292524] mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-[#B45309]" />
                    To import the tool into Aspire:
                  </h4>
                  <ol className="space-y-2 text-[#57534E]">
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                      <span>Open <strong className="text-[#292524]">Vectric Aspire</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                      <span>Go to <strong className="text-[#292524]">Toolpaths → Tool Database</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                      <span>Click <strong className="text-[#292524]">Import</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                      <span>Select the downloaded <strong className="text-[#292524]">.tools</strong> file</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#10B981] font-medium">The tool will appear in your database under:</p>
                      <p className="text-[#292524] font-mono text-sm mt-1">CNCHelpdesk</p>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-[#78716C]">
                  <strong className="text-[#B45309]">Tip:</strong> You can import multiple CNC-Helpdesk tools over time — Aspire will keep them organised by name.
                </div>
              </>
            )}

            {/* Fusion 360 Instructions */}
            {exportTarget === 'fusion360' && (
              <>
                <div className="bg-white/60 rounded-lg p-4 border border-[#D6D3D1]">
                  <h4 className="font-bold text-[#292524] mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-[#B45309]" />
                    To import the tool into Fusion 360:
                  </h4>
                  <ol className="space-y-2 text-[#57534E]">
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                      <span>Open <strong className="text-[#292524]">Fusion 360</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                      <span>Switch to the <strong className="text-[#292524]">Manufacture</strong> workspace</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                      <span>Open <strong className="text-[#292524]">Tool Library</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                      <span>Click <strong className="text-[#292524]">Import</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">5</span>
                      <span>Select the downloaded <strong className="text-[#292524]">.json</strong> file</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#10B981] font-medium">The tool will appear under:</p>
                      <p className="text-[#292524] font-mono text-sm mt-1">Local → CNCHelpdesk</p>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-[#78716C]">
                  <strong className="text-[#B45309]">Tip:</strong> You can now select this tool directly when creating toolpaths in Fusion 360.
                </div>
              </>
            )}

            {/* Carbide Create Instructions */}
            {exportTarget === 'carbide' && (
              <>
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 mb-4">
                  <p className="text-blue-400 text-sm">
                    <strong>Beginner Mode:</strong> Carbide Create does not import full tool libraries. 
                    You will create a custom tool manually using the settings provided.
                  </p>
                </div>

                <div className="bg-white/60 rounded-lg p-4 border border-[#D6D3D1]">
                  <h4 className="font-bold text-[#292524] mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-[#B45309]" />
                    To add this tool in Carbide Create:
                  </h4>
                  <ol className="space-y-2 text-[#57534E]">
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                      <span>Open <strong className="text-[#292524]">Carbide Create</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                      <span>Go to <strong className="text-[#292524]">Tool Database</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                      <span>Click <strong className="text-[#292524]">New Tool</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
                      <span>Enter the values from the downloaded <strong className="text-[#292524]">.csv</strong> file:</span>
                    </li>
                  </ol>
                  
                  <div className="mt-3 ml-7 bg-white/80 backdrop-blur-sm rounded p-2 text-xs text-[#78716C] space-y-1">
                    <div>• Tool Diameter</div>
                    <div>• Number of Flutes</div>
                    <div>• Feed Rate & Plunge Rate</div>
                    <div>• Spindle RPM</div>
                    <div>• Max Depth per Pass</div>
                    <div>• Stepover (Rough / Finish)</div>
                  </div>
                  
                  <div className="flex items-start gap-2 mt-3">
                    <span className="bg-[#B45309] text-[#0F172A] rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">5</span>
                    <span className="text-[#57534E]">Save the tool and use it in your toolpath</span>
                  </div>
                </div>

                <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#10B981] font-medium">The CSV file is:</p>
                      <ul className="text-[#292524] text-sm mt-1 space-y-1">
                        <li>• Human-readable & printable</li>
                        <li>• Easy to copy values from</li>
                        <li>• Beginner-safe settings</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-[#78716C]">
                  <strong className="text-[#B45309]">Tip:</strong> Start with shallow cuts if this is your first custom tool. You can always increase depth once you are confident.
                </div>
              </>
            )}

            {/* File Preview */}
            <div className="bg-white/60 rounded-lg p-3 border border-[#D6D3D1]">
              <p className="text-xs text-[#78716C] mb-1">File to download:</p>
              <p className="text-sm font-mono text-[#B45309]">
                {getExportFilename()}
              </p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowExportModal(false)}
              className="flex-1 border-[#D6D3D1] text-[#78716C] hover:text-[#292524] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmedDownload}
              className="flex-1 bg-[#10B981] hover:bg-[#059669] text-[#292524] font-bold"
            >
              <Download className="w-4 h-4 mr-2" />
              I Understand – Download {exportTarget === 'fusion360' ? 'Fusion 360' : exportTarget === 'carbide' ? 'Carbide Create' : 'Aspire'} Tool File
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Cut Modal */}
      <CNCTestCutModal
        calculatedSettings={result ? {
          feed_rate: result.feed_rate,
          plunge_rate: result.plunge_rate,
          spindle_rpm: result.spindle_rpm,
          pass_depth: result.pass_depth
        } : null}
        material={getMaterialName()}
        toolDiameter={formData.tool_diameter}
        isOpen={showTestCutModal}
        onClose={() => setShowTestCutModal(false)}
      />
    </div>
  );
};

export default Calculator;
